import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo, useState } from 'react';
import CvnaCategoryName from '~/constants/vdp/cvnaCategoryName';
import ShowcasePage from '~/constants/vdp/showcasePage';
import type { SpinnerCategory, SpinnerData } from '~/contracts/vdp';
import type { DeepPartial } from '~/types';

export type ContextType = {
  activeCategory?: CvnaCategoryName;
  activePage?: ShowcasePage;
  openToImageId?: string;
  setActiveCategory: React.Dispatch<
    React.SetStateAction<CvnaCategoryName | undefined>
  >;
  setActivePage: React.Dispatch<React.SetStateAction<ShowcasePage | undefined>>;
  setOpenToImageId: React.Dispatch<React.SetStateAction<string | undefined>>;
  spinnerData: LoadableState<SpinnerData>;
};

export const defaultContext: ContextType = {
  activeCategory: undefined,
  activePage: undefined,
  openToImageId: undefined,
  setActiveCategory() {},
  setActivePage() {},
  setOpenToImageId() {},
  spinnerData: defaultLoadableState<SpinnerData>({
    car: {
      headlines: ''
    },
    categories: [],
    spins: [],
    interiorImages: [],
    features: [],
    imperfections: []
  })
};

const ShowcaseContext = createContext<ContextType>(defaultContext);

export function ShowcaseContextProvider({ children }: React.PropsWithChildren) {
  const [activeCategory, setActiveCategory] = useState<
    CvnaCategoryName | undefined
  >(undefined);
  const [activePage, setActivePage] = useState<ShowcasePage | undefined>(
    undefined
  );
  const [openToImageId, setOpenToImageId] = useState(
    defaultContext.openToImageId
  );
  const spinnerData = useLoadableState(defaultContext.spinnerData.data);

  const value = useMemo<ContextType>(
    (): ContextType => ({
      activeCategory,
      activePage,
      openToImageId,
      setActiveCategory,
      setActivePage,
      setOpenToImageId,
      spinnerData
    }),
    [activeCategory, activePage, openToImageId, spinnerData]
  );

  return (
    <ShowcaseContext.Provider value={value}>
      {children}
    </ShowcaseContext.Provider>
  );
}

ShowcaseContextProvider.displayName = 'ShowcaseContextProvider';

export const generateShowcaseContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useSpinnerData(): SpinnerData {
  return useContext(ShowcaseContext).spinnerData.data;
}

export function useSpinnerDataCategories(): readonly SpinnerCategory[] {
  return useSpinnerData().categories ?? [];
}

export function useGetVehicleHeadline(): string {
  return useSpinnerData().car?.headlines ?? '';
}

export default ShowcaseContext;
